import React, { useState } from 'react';
import './Settings.css';

interface UserProfile {
  name: string;
  partnerName: string;
  anniversary: string;
  avatar: string;
  partnerAvatar: string;
  relationshipStatus: string;
  bio: string;
}

interface AppSettings {
  theme: 'light' | 'dark' | 'auto';
  language: 'zh' | 'en';
  notifications: {
    anniversary: boolean;
    goalReminder: boolean;
    emotionReminder: boolean;
    weeklyReport: boolean;
  };
  privacy: {
    publicProfile: boolean;
    shareMemories: boolean;
    dataBackup: boolean;
  };
  dataManagement: {
    autoBackup: boolean;
    backupFrequency: 'daily' | 'weekly' | 'monthly';
    storageLocation: 'local' | 'cloud';
  };
}

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'profile' | 'preferences' | 'privacy' | 'data' | 'about'>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const [profile, setProfile] = useState<UserProfile>({
    name: '小明',
    partnerName: '小红',
    anniversary: '2023-02-14',
    avatar: '/api/placeholder/80/80',
    partnerAvatar: '/api/placeholder/80/80',
    relationshipStatus: '恋爱中',
    bio: '我们的爱情故事从这里开始...'
  });

  const [settings, setSettings] = useState<AppSettings>({
    theme: 'light',
    language: 'zh',
    notifications: {
      anniversary: true,
      goalReminder: true,
      emotionReminder: false,
      weeklyReport: true
    },
    privacy: {
      publicProfile: false,
      shareMemories: true,
      dataBackup: true
    },
    dataManagement: {
      autoBackup: true,
      backupFrequency: 'weekly',
      storageLocation: 'local'
    }
  });

  const tabs = [
    { id: 'profile', label: '个人资料', icon: '👤' },
    { id: 'preferences', label: '偏好设置', icon: '⚙️' },
    { id: 'privacy', label: '隐私安全', icon: '🔒' },
    { id: 'data', label: '数据管理', icon: '💾' },
    { id: 'about', label: '关于应用', icon: 'ℹ️' }
  ];

  const handleProfileUpdate = (field: keyof UserProfile, value: string) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleSettingsUpdate = (section: keyof AppSettings, field: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...(prev[section] as any),
        [field]: value
      }
    }));
  };

  const handleAvatarUpload = (type: 'user' | 'partner') => {
    // 模拟头像上传
    const newAvatar = `/api/placeholder/80/80?${Date.now()}`;
    if (type === 'user') {
      handleProfileUpdate('avatar', newAvatar);
    } else {
      handleProfileUpdate('partnerAvatar', newAvatar);
    }
  };

  const handleExportData = () => {
    // 模拟数据导出
    const data = {
      profile,
      settings,
      exportDate: new Date().toISOString(),
      version: '1.0.0'
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `couple-memories-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportModal(false);
  };

  const handleImportData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target?.result as string);
          if (data.profile) setProfile(data.profile);
          if (data.settings) setSettings(data.settings);
          alert('数据导入成功！');
        } catch (error) {
          alert('数据格式错误，导入失败！');
        }
      };
      reader.readAsText(file);
    }
    setShowImportModal(false);
  };

  const calculateDaysInLove = () => {
    const anniversary = new Date(profile.anniversary);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - anniversary.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const handleSyncData = () => {
    // 这里可以添加数据同步逻辑
    console.log('同步数据');
    alert('数据同步成功！');
  };

  const handleFeedback = () => {
    // 这里可以添加意见反馈逻辑
    console.log('意见反馈');
    window.open('mailto:feedback@example.com?subject=应用反馈', '_blank');
  };

  const handleRateApp = () => {
    // 这里可以添加应用评价逻辑
    console.log('评价应用');
    alert('感谢您的评价！');
  };

  const handleHelp = () => {
    // 这里可以添加使用帮助逻辑
    console.log('使用帮助');
    alert('帮助文档即将打开');
  };

  const renderProfileTab = () => (
    <div className="settings-section">
      <div className="section-header">
        <h3>个人资料</h3>
        <button 
          className={`btn ${isEditing ? 'btn-secondary' : 'btn-primary'}`}
          onClick={() => setIsEditing(!isEditing)}
        >
          {isEditing ? '保存' : '编辑'}
        </button>
      </div>

      <div className="profile-avatars">
        <div className="avatar-group">
          <div className="avatar-item">
            <img src={profile.avatar} alt="我的头像" className="avatar" />
            {isEditing && (
              <button 
                className="avatar-upload-btn"
                onClick={() => handleAvatarUpload('user')}
              >
                📷
              </button>
            )}
            <span className="avatar-label">{profile.name}</span>
          </div>
          
          <div className="love-heart">💕</div>
          
          <div className="avatar-item">
            <img src={profile.partnerAvatar} alt="TA的头像" className="avatar" />
            {isEditing && (
              <button 
                className="avatar-upload-btn"
                onClick={() => handleAvatarUpload('partner')}
              >
                📷
              </button>
            )}
            <span className="avatar-label">{profile.partnerName}</span>
          </div>
        </div>
        
        <div className="love-stats">
          <div className="stat-item">
            <span className="stat-number">{calculateDaysInLove()}</span>
            <span className="stat-label">相爱天数</span>
          </div>
        </div>
      </div>

      <div className="profile-form">
        <div className="form-row">
          <div className="form-group">
            <label>我的昵称</label>
            <input
              type="text"
              value={profile.name}
              onChange={(e) => handleProfileUpdate('name', e.target.value)}
              disabled={!isEditing}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>TA的昵称</label>
            <input
              type="text"
              value={profile.partnerName}
              onChange={(e) => handleProfileUpdate('partnerName', e.target.value)}
              disabled={!isEditing}
              className="form-input"
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>纪念日</label>
            <input
              type="date"
              value={profile.anniversary}
              onChange={(e) => handleProfileUpdate('anniversary', e.target.value)}
              disabled={!isEditing}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>关系状态</label>
            <select
              value={profile.relationshipStatus}
              onChange={(e) => handleProfileUpdate('relationshipStatus', e.target.value)}
              disabled={!isEditing}
              className="form-select"
            >
              <option value="恋爱中">恋爱中</option>
              <option value="订婚">订婚</option>
              <option value="已婚">已婚</option>
              <option value="其他">其他</option>
            </select>
          </div>
        </div>
        
        <div className="form-group">
          <label>爱情宣言</label>
          <textarea
            value={profile.bio}
            onChange={(e) => handleProfileUpdate('bio', e.target.value)}
            disabled={!isEditing}
            className="form-textarea"
            rows={3}
            placeholder="写下你们的爱情故事..."
          />
        </div>
      </div>
    </div>
  );

  const renderPreferencesTab = () => (
    <div className="settings-section">
      <h3>偏好设置</h3>
      
      <div className="setting-group">
        <h4>外观设置</h4>
        <div className="setting-item">
          <label>主题模式</label>
          <select
            value={settings.theme}
            onChange={(e) => handleSettingsUpdate('theme', 'theme', e.target.value)}
            className="form-select"
          >
            <option value="light">浅色模式</option>
            <option value="dark">深色模式</option>
            <option value="auto">跟随系统</option>
          </select>
        </div>
        
        <div className="setting-item">
          <label>语言设置</label>
          <select
            value={settings.language}
            onChange={(e) => handleSettingsUpdate('language', 'language', e.target.value)}
            className="form-select"
          >
            <option value="zh">中文</option>
            <option value="en">English</option>
          </select>
        </div>
      </div>
      
      <div className="setting-group">
        <h4>通知设置</h4>
        {Object.entries({
          anniversary: '纪念日提醒',
          goalReminder: '目标提醒',
          emotionReminder: '情感记录提醒',
          weeklyReport: '周报推送'
        }).map(([key, label]) => (
          <div key={key} className="setting-item">
            <label>{label}</label>
            <label className="switch">
              <input
                type="checkbox"
                checked={settings.notifications[key as keyof typeof settings.notifications]}
                onChange={(e) => handleSettingsUpdate('notifications', key, e.target.checked)}
              />
              <span className="slider"></span>
            </label>
          </div>
        ))}
      </div>
    </div>
  );

  const renderPrivacyTab = () => (
    <div className="settings-section">
      <h3>隐私安全</h3>
      
      <div className="setting-group">
        <h4>隐私设置</h4>
        {Object.entries({
          publicProfile: '公开个人资料',
          shareMemories: '允许分享回忆',
          dataBackup: '数据备份'
        }).map(([key, label]) => (
          <div key={key} className="setting-item">
            <label>{label}</label>
            <label className="switch">
              <input
                type="checkbox"
                checked={settings.privacy[key as keyof typeof settings.privacy]}
                onChange={(e) => handleSettingsUpdate('privacy', key, e.target.checked)}
              />
              <span className="slider"></span>
            </label>
          </div>
        ))}
      </div>
      
      <div className="setting-group">
        <h4>安全操作</h4>
        <div className="security-actions">
          <button className="btn btn-secondary">
            🔑 修改密码
          </button>
          <button className="btn btn-secondary">
            📱 双重验证
          </button>
          <button className="btn btn-danger">
            🗑️ 清除所有数据
          </button>
        </div>
      </div>
    </div>
  );

  const renderDataTab = () => (
    <div className="settings-section">
      <h3>数据管理</h3>
      
      <div className="setting-group">
        <h4>备份设置</h4>
        <div className="setting-item">
          <label>自动备份</label>
          <label className="switch">
            <input
              type="checkbox"
              checked={settings.dataManagement.autoBackup}
              onChange={(e) => handleSettingsUpdate('dataManagement', 'autoBackup', e.target.checked)}
            />
            <span className="slider"></span>
          </label>
        </div>
        
        <div className="setting-item">
          <label>备份频率</label>
          <select
            value={settings.dataManagement.backupFrequency}
            onChange={(e) => handleSettingsUpdate('dataManagement', 'backupFrequency', e.target.value)}
            className="form-select"
          >
            <option value="daily">每日</option>
            <option value="weekly">每周</option>
            <option value="monthly">每月</option>
          </select>
        </div>
        
        <div className="setting-item">
          <label>存储位置</label>
          <select
            value={settings.dataManagement.storageLocation}
            onChange={(e) => handleSettingsUpdate('dataManagement', 'storageLocation', e.target.value)}
            className="form-select"
          >
            <option value="local">本地存储</option>
            <option value="cloud">云端存储</option>
          </select>
        </div>
      </div>
      
      <div className="setting-group">
        <h4>数据操作</h4>
        <div className="data-actions">
          <button 
            className="btn btn-primary"
            onClick={() => setShowExportModal(true)}
          >
            📤 导出数据
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => setShowImportModal(true)}
          >
            📥 导入数据
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => handleSyncData()}
          >
            🔄 同步数据
          </button>
        </div>
      </div>
      
      <div className="data-stats">
        <h4>存储统计</h4>
        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-icon">📸</span>
            <div className="stat-content">
              <span className="stat-number">156</span>
              <span className="stat-label">回忆照片</span>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-icon">🎯</span>
            <div className="stat-content">
              <span className="stat-number">23</span>
              <span className="stat-label">目标计划</span>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-icon">💭</span>
            <div className="stat-content">
              <span className="stat-number">89</span>
              <span className="stat-label">情感记录</span>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-icon">💾</span>
            <div className="stat-content">
              <span className="stat-number">12.5MB</span>
              <span className="stat-label">总存储</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAboutTab = () => (
    <div className="settings-section">
      <h3>关于应用</h3>
      
      <div className="about-content">
        <div className="app-info">
          <div className="app-icon">💕</div>
          <h2>情侣甜蜜记录</h2>
          <p className="version">版本 1.0.0</p>
          <p className="description">
            专为情侣打造的甜蜜记录应用，帮助你们记录美好时光，规划未来，分享彼此的情感世界。
          </p>
        </div>
        
        <div className="feature-list">
          <h4>主要功能</h4>
          <ul>
            <li>📸 回忆相册 - 记录美好瞬间</li>
            <li>🎯 目标规划 - 共同规划未来</li>
            <li>💭 情感记录 - 分享内心世界</li>
            <li>📊 数据统计 - 了解彼此更多</li>
            <li>🔒 隐私保护 - 安全可靠</li>
          </ul>
        </div>
        
        <div className="contact-info">
          <h4>联系我们</h4>
          <div className="contact-links">
            <button 
              className="btn btn-secondary"
              onClick={() => handleFeedback()}
            >
              📧 意见反馈
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => handleRateApp()}
            >
              ⭐ 评价应用
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => handleHelp()}
            >
              📖 使用帮助
            </button>
          </div>
        </div>
        
        <div className="legal-info">
          <p>
            <a href="#" onClick={(e) => { e.preventDefault(); alert('隐私政策页面'); }}>隐私政策</a> | 
            <a href="#" onClick={(e) => { e.preventDefault(); alert('服务条款页面'); }}>服务条款</a> | 
            <a href="#" onClick={(e) => { e.preventDefault(); alert('开源许可页面'); }}>开源许可</a>
          </p>
          <p className="copyright">© 2024 情侣甜蜜记录. All rights reserved.</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="settings">
      <div className="settings-header">
        <h2 className="settings-title">设置</h2>
      </div>
      
      <div className="settings-container">
        <div className="settings-sidebar">
          <nav className="settings-nav">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id as any)}
              >
                <span className="nav-icon">{tab.icon}</span>
                <span className="nav-label">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
        
        <div className="settings-content">
          {activeTab === 'profile' && renderProfileTab()}
          {activeTab === 'preferences' && renderPreferencesTab()}
          {activeTab === 'privacy' && renderPrivacyTab()}
          {activeTab === 'data' && renderDataTab()}
          {activeTab === 'about' && renderAboutTab()}
        </div>
      </div>
      
      {/* 导出数据模态框 */}
      {showExportModal && (
        <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>导出数据</h3>
              <button 
                className="close-btn"
                onClick={() => setShowExportModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-content">
              <p>确定要导出所有数据吗？导出的文件将包含：</p>
              <ul>
                <li>个人资料信息</li>
                <li>所有回忆记录</li>
                <li>目标计划数据</li>
                <li>情感记录数据</li>
                <li>应用设置</li>
              </ul>
              <div className="modal-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowExportModal(false)}
                >
                  取消
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleExportData}
                >
                  确认导出
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* 导入数据模态框 */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>导入数据</h3>
              <button 
                className="close-btn"
                onClick={() => setShowImportModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-content">
              <p>选择要导入的数据文件：</p>
              <div className="file-upload">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportData}
                  className="file-input"
                  id="import-file"
                />
                <label htmlFor="import-file" className="file-label">
                  📁 选择文件
                </label>
              </div>
              <p className="warning">
                ⚠️ 导入数据将覆盖当前所有数据，请确保已备份重要信息。
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;